import { useEffect, useRef, useState } from "react";

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

import { withTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import { isEqual } from "lodash";
import i18next from "@plannel/utils/i18n";

import systemService from "@plannel/services/system/system-service";
import { RouteList } from "@plannel/routeList";
import PlannelFooter from "@plannel/components/PlannelFooter";
import Dialog from "@plannel/components/Dialog"
import SelectLanguage from "@plannel/components/SelectLanguage"
import PasswordInput from "@plannel/components/PasswordInput";
import BackToSignIn from "@plannel/components/BackToSignIn";
import PasswordPolicyContainer from "@plannel/components/PasswordPolicyContainer";
import logo from "@plannel/assets/icons/main-logo.png";
import debug from "@plannel/utils/debug-util";

const ResetPassword = ({ t }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useRef({ tenant: "", username: "", passwordResetToken: "" });
  const [isValidToken, setIsValidToken] = useState(true);

  const [showPassword, setShowPassword] = useState({ newPassword: false, confirmPassword: false });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [msgObj, setMsgObj] = useState({ message: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const verifyToken = () => {
    systemService.verifyPasswordResetToken(searchParams.current)
      .catch(error => {
        debug(error);
        const { message } = error.response.data;
        setMsgObj({ message });
        setIsValidToken(false);
      });
  }

  const handleErrorResponse = error => {
    const { data } = error.response;
    const errorMessage = data?.message ? data.message : "MSG_NetworkError";

    debug(error);
    setMsgObj({
      ...msgObj,
      ...data,
      message: errorMessage
    });
  }

  const resetPassword = () => {
    const { username } = searchParams.current;
    const params = { username, newPassword, confirmPassword, changePasswordFlg: true, resetPasswordFlg: true };
    systemService.changePassword(searchParams.current.tenant, params)
      .then(() => {
        setDialogInfo({ open: true, title: "info", content: t("MSG_HasBeenChanged") });
      })
      .catch(error => handleErrorResponse(error));
  }

  const setErrorMessage = () => {
    if (i18next.exists(msgObj.message)) {
      const messageWithCount = ["MSG_PasswordDCredit", "MSG_PasswordLCredit", "MSG_PasswordOCredit", "MSG_PasswordUCredit", "MSG_PasswordRepeat"];
      if (msgObj?.details?.count && messageWithCount.includes(msgObj.message)) {
        return t(msgObj.message, { N: msgObj.details.count });
      }
      if (msgObj?.details?.min && msgObj?.details?.max && isEqual(msgObj.message, "MSG_CheckPasswordLength")) {
        return t(msgObj.message, { min: msgObj.details.min, max: msgObj.details.max });
      }

      return t(msgObj.message);
    }

    return t("MSG_NetworkError");
  }

  const handleDialog = (action) => {
    if (action === "INFO") {
      navigate(RouteList.Signin.path);
      window.location.reload();
    }
  }

  useEffect(() => {
    if (!location?.search) {
      navigate(RouteList.Signin.path);
    } else {
      const urlSearchParams = new URLSearchParams(location.search);
      const username = urlSearchParams.get("username");
      const passwordResetToken = urlSearchParams.get("token");
      const tenant = urlSearchParams.get("tenant");

      searchParams.current = { tenant, username, passwordResetToken };
      verifyToken();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container id="app-home" component="main" maxWidth="xs">
      <CssBaseline />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }} >
        <Box mt={8}>
          <img src={logo} width={170} alt="loading..." />
        </Box>
        <Typography component="h1" variant="h5" my={1}>{t("resetPassword")}</Typography>
        <Box sx={{ width: isValidToken ? "auto" : "100%" }}>
          {
            isValidToken ? (
              <>
                <Typography variant="body2" sx={{ textAlign: "center", mb: 1 }}>{t("MSG_EnterNewPassword")}</Typography>
                <PasswordInput
                  name="newPassword"
                  label={t("newPassword")}
                  options={{ margin: "normal" }}
                  password={newPassword}
                  setPassword={setNewPassword}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  withVisibilityToggle />
                <PasswordInput
                  name="confirmPassword"
                  label={t("newPasswordConfirmation")}
                  options={{ margin: "normal" }}
                  password={confirmPassword}
                  setPassword={setConfirmPassword}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  withVisibilityToggle />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 2 }} onClick={resetPassword}>{t("confirm")}</Button>
              </>
            ) : null
          }
          {Boolean(msgObj.message) && (
            <Alert sx={{ mb: 2, mt: isValidToken ? 0 : 1 }} severity="error">{setErrorMessage()}</Alert>
          )}
          {isValidToken ? <PasswordPolicyContainer /> : null}
          <Box sx={{ mt: 1, display: "flex" }}>
            <SelectLanguage />
            <BackToSignIn />
          </Box>
        </Box>
      </Box>
      <PlannelFooter/>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </Container>
  );
};

export default withTranslation()(ResetPassword);